import { freshChartEvidence } from '../research/agentSignals';
import { projectSampleSignals } from './sampleSignals';

// Read-only display projection. Source prices/times/decisions are never rewritten.
export function projectLiveSignals(chart, snapshot, technical = [], agents = [], now = Date.now()) {
  if (snapshot?.evidence_kind === 'daily_price_samples') return projectSampleSignals(chart, snapshot, now);
  const empty = { matched: false, items: [], inRange: [], outside: 0, before: 0, after: 0, invalid: 0 };
  if (!Array.isArray(snapshot?.bars) || !snapshot.bars.length || snapshot.bars.some(bar => !bar || !Number.isFinite(bar.time) || !Number.isFinite(bar.close))
      || !chart?.research_symbol || snapshot?.asset !== 'crypto' || snapshot.currency !== 'USD'
      || chart.currency !== 'USD' || snapshot.symbol !== chart.research_symbol || !freshChartEvidence(snapshot, now)) return empty;
  const points = chart.points;
  if (!Array.isArray(points) || !points.length || points.some((point,i) => !point || !Number.isFinite(point.time) || !Number.isFinite(point.value) || point.value <= 0 || i && point.time <= points[i-1].time)) return empty;
  const bars = new Map(snapshot.bars.map(bar => [bar.time, bar]));
  const items = [], seen = new Set(); let invalid = 0;
  const accept = (signal, kind) => {
    if (!signal || typeof signal !== 'object' || !Number.isFinite(signal.time) || typeof signal.id !== 'string' || !signal.id) { invalid++; return; }
    const bar = bars.get(signal.time);
    const reference = signal.reference_price;
    const completed = signal.time + 86400;
    const reviewed = kind === 'ai' ? Date.parse(signal.reviewed_at) / 1000 : completed;
    const available = Math.max(completed, reviewed);
    if (!bar || !Number.isFinite(reference) || reference <= 0 || reference !== bar.close || !signal.id
        || !Number.isFinite(available) || available > now / 1000
        || kind === 'ai' && (!['vibe', 'technical', 'risk'].includes(signal.agent) || reviewed < completed)
        || !['buy', 'sell', ...(kind === 'ai' ? ['hold'] : [])].includes(signal.action)
        || kind === 'technical' && (!['breakout', 'pullback'].includes(signal.setup) || signal.confirmation !== 'Completed daily close')) { invalid++; return; }
    const id = `${kind}:${signal.id}`;
    if (seen.has(id)) return;
    seen.add(id);
    let anchor = null;
    // Do not clamp an old event onto the first visible price sample, or backdate an AI review.
    if (available >= points[0].time && available <= points.at(-1).time) {
      let lo = 0, hi = points.length - 1;
      while (lo < hi) { const mid = (lo + hi) >> 1; if (points[mid].time < available) lo = mid + 1; else hi = mid; }
      anchor = points[lo].time;
    }
    items.push({ ...signal, id, kind, source_time: signal.time, available_at: available, plot_time: anchor,
      source_provider: snapshot.provider, source_timestamp: snapshot.source_timestamp });
  };
  if (Array.isArray(technical)) technical.forEach(signal => accept(signal, 'technical')); else invalid++;
  if (Array.isArray(agents)) agents.forEach(signal => accept(signal, 'ai')); else invalid++;
  items.sort((a,b) => a.available_at-b.available_at || a.id.localeCompare(b.id));
  return { matched: true, items, inRange: items.filter(s => s.plot_time !== null), outside: items.filter(s => s.plot_time === null).length,
    before: items.filter(s => s.available_at < points[0].time).length, after: items.filter(s => s.available_at > points.at(-1).time).length, invalid };
}

export function liveSeriesMarkers(items, translate) {
  return items.map(item => ({ time: item.plot_time, position: item.action === 'buy' ? 'belowBar' : 'aboveBar',
    color: item.action === 'buy' ? '#4ade80' : item.action === 'sell' ? '#fb7185' : '#e9c173',
    shape: item.action === 'buy' ? 'arrowUp' : item.action === 'sell' ? 'arrowDown' : 'circle',
    text: `${item.kind === 'ai' ? (item.name || 'AI') + ' · ' : ''}${translate(item.action === 'buy' ? 'Buy' : item.action === 'sell' ? 'Sell' : 'Hold')}`, size: 1,
  })).sort((a,b) => a.time-b.time);
}